import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '../../../utils';

// ============================================================================
// Radio Group - Container for radio items
// ============================================================================

export interface RadioGroupProps extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  /** Orientation of the radio group */
  orientation?: 'horizontal' | 'vertical';
}

export const RadioGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn(
      'grid gap-2',
      orientation === 'horizontal' && 'grid-flow-col auto-cols-max',
      className
    )}
    {...props}
  />
));
RadioGroup.displayName = 'RadioGroup';

// ============================================================================
// Radio Group Item - Individual radio button
// ============================================================================

export interface RadioGroupItemProps extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  /** Label for the radio item */
  label?: string;
}

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, id, ...props }, ref) => {
  const radioId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex items-center gap-2">
      <RadioGroupPrimitive.Item
        ref={ref}
        id={radioId}
        className={cn(
          'h-5 w-5 shrink-0 rounded-full border border-border-hover',
          'bg-[var(--field-bg)]',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring-color)] focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-[var(--action-primary)]',
          'transition-colors',
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="h-2.5 w-2.5 fill-[var(--action-primary)] text-[var(--action-primary)]" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && (
        <label
          htmlFor={radioId}
          className="text-sm text-text-secondary cursor-pointer"
        >
          {label}
        </label>
      )}
    </div>
  );
});
RadioGroupItem.displayName = 'RadioGroupItem';
